import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useState } from 'react';

import { type PixelType, usePixelList } from '@/api/pixel';
import {
  InstantSearch,
  InstantSearchSortOrder,
  type QueryState,
  type SolQueryParams,
} from '@/components/InstantSearch';
import { getSolQueryParamsFromQueryState } from '@/components/InstantSearch/util/search-util';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import useDisplayNotificationOnLoad from '@/hooks/useDisplayNotificationOnLoad';
import useEffectOnceWhenDepsExist from '@/hooks/useEffectOnceWhenDepsExist';
import useLocationState from '@/hooks/useLocationState';

import PixelsListHeader from './PixelsListHeader';
import PixelsListTable from './PixelsListTable';
import PixelUsageChart from './PixelUsageChart';

const DEFAULT_QUERY_STATE: Partial<QueryState> = {
  size: 10,
  sortBy: 'updated_at',
  sortOrder: InstantSearchSortOrder.DESC,
};

const PixelsOverview = () => {
  const [tableParams, setTableParams] = useState<SolQueryParams>();
  const { successMessage, optimisticUpdate } = useLocationState<{
    successMessage: string;
    optimisticUpdate: PixelType;
  }>();
  const {
    pixelListData,
    totalResults,
    isLoading,
    error,
    mutate: mutateListData,
  } = usePixelList(tableParams);

  useDisplayNotificationOnLoad({ type: 'success', message: successMessage });
  useEffectOnceWhenDepsExist(() => {
    if (pixelListData && totalResults) {
      mutateListData({
        pixels: {
          get: {
            edges: pixelListData?.map((pixel) =>
              pixel.id === optimisticUpdate.id
                ? { node: { ...pixel, ...optimisticUpdate } }
                : { node: pixel },
            ),
            totalEdges: totalResults,
          },
        },
      });
    }
  }, [optimisticUpdate, pixelListData, mutateListData]);

  const handleQueryStateChange = useCallback(
    (queryState: Required<QueryState>) => {
      const tableParams = getSolQueryParamsFromQueryState(queryState);
      setTableParams(tableParams);
    },
    [setTableParams],
  );

  return (
    <Page title={t`Pixel Overview`} exampleAIQuestions={exampleAIQuestions('pixel')}>
      <div className="flex flex-col gap-6">
        <InstantSearch
          prefix="p"
          defaultQueryState={DEFAULT_QUERY_STATE}
          onQueryStateChange={handleQueryStateChange}
        >
          <div className="flex w-full flex-col gap-6">
            <PixelsListHeader />
            <Panel title={<Trans>Pixels</Trans>} data-testid="pixels-table-panel" isFullHeight>
              <PixelsListTable
                data={pixelListData}
                totalResults={totalResults}
                isLoading={isLoading}
                error={error}
              />
            </Panel>
          </div>
        </InstantSearch>
        <Panel title={<Trans>Pixel Usage</Trans>} noPadding>
          <div style={{ height: 600 }}>
            <PixelUsageChart />
          </div>
        </Panel>
      </div>
    </Page>
  );
};

export default PixelsOverview;
